import type { CreateInviteResponse, StudentDto } from '@mm/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createStudentInvite } from '../api/invites';
import { deleteStudent, getStudent } from '../api/students';
import { BoardsGrid } from '../components/BoardsGrid';
import { InviteModal } from '../components/InviteModal';
import { OnlineDot } from '../components/OnlineDot';
import { useStudentPresence } from '../hooks/useStudentPresence';
import { AppLayout } from '../layout/AppLayout';
import { Button } from '../ui/Button';
import { IconPencil } from '../ui/icons';

export function StudentCardPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState<StudentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [invite, setInvite] = useState<CreateInviteResponse | null>(null);
  const [inviting, setInviting] = useState(false);
  const online = useStudentPresence(student?.id);

  useEffect(() => {
    if (!id) return;
    getStudent(id)
      .then((s) => {
        setStudent(s);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [id]);

  async function onDelete() {
    if (!student) return;
    if (!confirm(`Удалить ученика «${student.fullName}»?`)) return;
    await deleteStudent(student.id);
    nav('/app/students');
  }

  async function onInvite() {
    if (!student) return;
    setInviting(true);
    try {
      const r = await createStudentInvite(student.id);
      setInvite(r);
    } catch {
      alert('Не удалось создать приглашение');
    } finally {
      setInviting(false);
    }
  }

  const crumbs =
    loading || notFound || !student
      ? [{ label: 'Админ' }, { to: '/app/students', label: 'Ученики' }, { label: '…' }]
      : [{ label: 'Админ' }, { to: '/app/students', label: 'Ученики' }, { label: student.fullName }];

  return (
    <AppLayout crumbs={crumbs}>
      <div className="max-w-5xl px-8 py-7">
        {loading ? (
          <p className="text-muted text-[13px]">Загрузка…</p>
        ) : notFound || !student ? (
          <div>
            <p>Ученик не найден.</p>
            <Link to="/app/students" className="text-muted hover:text-fg text-[12px]">
              ← к списку
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-1">
              <h1
                className="font-display text-[42px] leading-none font-medium flex items-center"
                style={{ fontVariationSettings: '"opsz" 96, "SOFT" 5' }}
              >
                {student.fullName}
                <OnlineDot online={online} className="ml-3 align-middle" />
              </h1>
              <div className="flex gap-2">
                <Button onClick={onInvite} disabled={inviting}>
                  {inviting
                    ? 'Создаём…'
                    : student.hasUser
                      ? 'Переслать доступ'
                      : 'Пригласить'}
                </Button>
                <Link to={`/app/students/${student.id}/edit`}>
                  <Button variant="outline">
                    <IconPencil size={13} />
                    Редактировать
                  </Button>
                </Link>
                <Button variant="ghost" onClick={onDelete} className="text-muted hover:text-accent">
                  Удалить
                </Button>
              </div>
            </div>
            <p className="font-caps italic text-[13px] text-muted mb-8">
              Карточка ученика
            </p>

            <dl className="grid grid-cols-[140px_1fr] gap-y-4 gap-x-6 max-w-2xl mb-10">
              <dt className="text-[11px] uppercase tracking-[0.1em] text-muted pt-1">Класс</dt>
              <dd className="text-[14px] font-serif-num">{student.grade ?? '—'}</dd>

              <dt className="text-[11px] uppercase tracking-[0.1em] text-muted pt-1">Email</dt>
              <dd className="text-[14px]">
                {student.email ? (
                  <a href={`mailto:${student.email}`} className="text-link hover:underline underline-offset-4">
                    {student.email}
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </dd>

              <dt className="text-[11px] uppercase tracking-[0.1em] text-muted pt-1">Телефон</dt>
              <dd className="text-[14px] nums">{student.phone ?? '—'}</dd>

              <dt className="text-[11px] uppercase tracking-[0.1em] text-muted pt-1">Заметки</dt>
              <dd className="text-[14px] whitespace-pre-wrap">
                {student.notes || <span className="text-muted">—</span>}
              </dd>
            </dl>

            <div className="pt-6 border-t border-border">
              <BoardsGrid studentId={student.id} />
            </div>

            {invite ? (
              <InviteModal
                url={invite.url}
                expiresAt={invite.expiresAt}
                warnReinvite={student.hasUser}
                onClose={() => {
                  setInvite(null);
                  // новая ссылка отвязала старый userId, перечитываем карточку
                  void getStudent(student.id).then(setStudent).catch(() => {});
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </AppLayout>
  );
}
